import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import { MEDAL_COLORS } from './WorldCupLeaderboard.constants';

export const getWorldCupLeaderboardStyles = (theme: Theme) =>
  StyleSheet.create({
    flashList: {
      flex: 1,
    },
    podiumRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'flex-end',
      gap: 8,
      paddingHorizontal: 16,
      paddingBottom: 20,
      paddingTop: 8,
    },
    podiumCard: {
      flex: 1,
      alignItems: 'center',
      paddingVertical: 14,
      paddingHorizontal: 6,
      backgroundColor: theme.background.card,
      borderRadius: 16,
      borderWidth: 1.5,
      borderColor: theme.gray[100],
      minHeight: 130,
      justifyContent: 'center',
    },
    podiumCardFirst: {
      minHeight: 160,
      borderColor: MEDAL_COLORS.gold + '80',
      backgroundColor: MEDAL_COLORS.gold + '0D',
      ...(Platform.OS === 'web' && {
        boxShadow: `0 0 24px 4px ${MEDAL_COLORS.gold}33` as never,
      }),
    },
    podiumCardSecond: {
      borderColor: MEDAL_COLORS.silver + '60',
      backgroundColor: MEDAL_COLORS.silver + '0A',
    },
    podiumCardThird: {
      borderColor: MEDAL_COLORS.bronze + '60',
      backgroundColor: MEDAL_COLORS.bronze + '0A',
    },
    podiumCardMe: {
      borderColor: theme.primary + '60',
      backgroundColor: theme.primary + '0A',
    },
    podiumMedal: {
      fontSize: 24,
      marginBottom: 6,
    },
    podiumAvatarFrame: {
      marginBottom: 6,
      borderWidth: 2,
      borderRadius: 999,
      borderColor: 'transparent',
      alignItems: 'center',
      justifyContent: 'center',
    },
    podiumAvatarFrameFirst: {
      borderColor: MEDAL_COLORS.gold,
      backgroundColor: MEDAL_COLORS.gold + '1A',
    },
    podiumAvatarFrameSecond: {
      borderColor: MEDAL_COLORS.silver,
    },
    podiumAvatarFrameThird: {
      borderColor: MEDAL_COLORS.bronze,
    },
    podiumAvatarFrameMe: {
      borderColor: theme.primary,
    },
    podiumName: {
      fontSize: 11,
      fontWeight: '700',
      color: theme.gray[800],
      textAlign: 'center',
      marginTop: 2,
    },
    podiumPoints: {
      fontSize: 12,
      fontWeight: '800',
      color: theme.gray[500],
      marginTop: 3,
    },
    podiumPointsFirst: {
      color: MEDAL_COLORS.gold,
    },
    divider: {
      height: 1,
      backgroundColor: theme.gray[100],
      marginHorizontal: 16,
      marginBottom: 6,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 10,
      gap: 10,
    },
    rowMe: {
      backgroundColor: theme.primary + '0C',
      marginHorizontal: 8,
      borderRadius: 10,
      paddingHorizontal: 12,
      borderLeftWidth: 3,
      borderLeftColor: theme.primary,
    },
    rankText: {
      fontSize: 13,
      fontWeight: '700',
      color: theme.gray[400],
      width: 26,
      textAlign: 'center',
    },
    rankTextGold: {
      color: MEDAL_COLORS.gold,
      fontWeight: '800',
    },
    rankTextSilver: {
      color: MEDAL_COLORS.silver,
      fontWeight: '800',
    },
    rankTextBronze: {
      color: MEDAL_COLORS.bronze,
      fontWeight: '800',
    },
    avatarSlotRow: {
      position: 'relative' as const,
      width: 40,
      height: 40,
      alignItems: 'center',
      justifyContent: 'center',
    },
    avatarSlotPodiumLarge: {
      position: 'relative' as const,
      width: 80,
      height: 80,
      alignItems: 'center',
      justifyContent: 'center',
    },
    avatarSlotPodiumSmall: {
      position: 'relative' as const,
      width: 64,
      height: 64,
      alignItems: 'center',
      justifyContent: 'center',
    },
    profileBadge: {
      position: 'absolute',
      right: -1,
      bottom: -1,
      width: 15,
      height: 15,
      borderRadius: 8,
      borderWidth: 1.5,
      borderColor: theme.background.card,
      backgroundColor: theme.gray[100],
      overflow: 'hidden',
      alignItems: 'center',
      justifyContent: 'center',
    },
    profileBadgeImage: {
      width: '100%',
      height: '100%',
    },
    profileBadgeInitials: {
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.gray[200],
    },
    profileBadgeInitialsText: {
      fontSize: 7,
      fontWeight: '800',
      color: theme.gray[700],
    },
    nameText: {
      flex: 1,
      fontSize: 13,
      fontWeight: '600',
      color: theme.gray[800],
    },
    nameTextMe: {
      color: theme.primary,
      fontWeight: '700',
    },
    futuresWrap: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      marginRight: 6,
    },
    futureCell: {
      minWidth: 28,
      alignItems: 'center',
      justifyContent: 'center',
    },
    leaderboardKitOuter: {
      width: 32,
      height: 34,
      overflow: 'hidden',
      alignItems: 'center',
      justifyContent: 'center',
    },
    futurePlaceholder: {
      fontSize: 12,
      color: theme.gray[400],
      fontWeight: '600',
    },
    pointsText: {
      fontSize: 13,
      fontWeight: '700',
      color: theme.gray[600],
      width: 58,
      textAlign: 'right',
    },
    trendUp: {
      fontSize: 12,
      fontWeight: '700',
      color: theme.success,
    },
    trendDown: {
      fontSize: 12,
      fontWeight: '700',
      color: theme.error,
    },
    trendSame: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.gray[400],
    },
    separator: {
      height: 1,
      backgroundColor: theme.gray[100],
      marginLeft: 78,
    },
    pinnedDivider: {
      height: 1,
      backgroundColor: theme.gray[100],
      marginHorizontal: 16,
      marginVertical: 6,
    },
    listFooter: {
      paddingBottom: 100,
    },
    listFooterEmbedded: {
      paddingBottom: 0,
    },
    /** Padding above glass cards when embedded in a parent ScrollView (matches Leaderboard listContent top). */
    leaderboardScrollBody: {
      paddingTop: 4,
    },
    /** Wraps the row skeletons rendered during a Week ↔ Overall swap. Matches gap used by GroupDetailSkeleton. */
    rowsSkeletonContainer: {
      gap: 10,
      paddingHorizontal: 4,
    },
  });
